import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Table, TableHeader, TableHeaderCell, TableBody, TableRow, TableCell, Button, Spinner, Text, Badge, MessageBar, MessageBarBody, Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions, Field, Input, Dropdown, Option, Textarea, TabList, Tab, } from "@fluentui/react-components";
import { AddRegular, DismissCircleRegular, CalendarMonthRegular, ClipboardTaskList16Regular } from "@fluentui/react-icons";
import { useAuth } from "../../../context/AuthContext";
import { useDemo } from "../../../context/DemoContext";
import { PlanningView } from "../Planning/PlanningView";
import { getAllLeaveRequests, createLeaveRequest, updateLeaveRequestStatus, getAllApprobateurs, } from "../../../services/dataService";
const LEAVE_TYPES = [
    { value: "CP", label: "Congés payés" },
    { value: "RTT", label: "RTT" },
    { value: "JA", label: "Jours d'ancienneté" },
    { value: "CSS", label: "Congé sans solde" },
    { value: "CSF", label: "Congé solidarité familiale" },
    { value: "CPP", label: "Congé parentalité" },
    { value: "EFDEM", label: "Événement familial (déménagement)" },
    { value: "EFMAR", label: "Événement familial (mariage)" },
    { value: "REC", label: "Récupération" },
    { value: "RECJP", label: "Récupération jour de pont" },
    { value: "TELET", label: "Télétravail" },
    { value: "MISS", label: "Mission" },
];
function formatDateFR(iso) {
    if (!iso)
        return "";
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function countBusinessDays(start, end) {
    let count = 0;
    const cur = new Date(start);
    const endD = new Date(end);
    while (cur <= endD) {
        const dow = cur.getDay();
        if (dow !== 0 && dow !== 6)
            count++;
        cur.setDate(cur.getDate() + 1);
    }
    return count;
}
export const MyAbsences = () => {
    const { apiToken, userEmail, userName } = useAuth();
    const { demoUser } = useDemo();
    const effectiveEmail = demoUser?.email || userEmail;
    const effectiveName = demoUser?.name || userName;
    const [requests, setRequests] = useState([]);
    const [approbateurs, setApprobateurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [leaveType, setLeaveType] = useState("CP");
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);
    const [absTab, setAbsTab] = useState("planning");
    const showMessage = (msg, intent) => {
        if (intent === "success") {
            setSuccess(msg);
            setError(null);
        }
        else {
            setError(msg);
            setSuccess(null);
        }
        setTimeout(() => { setSuccess(null); setError(null); }, 5000);
    };
    const fetchData = useCallback(async () => {
        if (!apiToken)
            return;
        setLoading(true);
        try {
            const [reqs, approbs] = await Promise.all([
                getAllLeaveRequests(apiToken),
                getAllApprobateurs(apiToken),
            ]);
            setRequests(reqs);
            setApprobateurs(approbs);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Erreur");
        }
        finally {
            setLoading(false);
        }
    }, [apiToken]);
    useEffect(() => { fetchData(); }, [fetchData]);
    const myRequests = useMemo(() => {
        const email = effectiveEmail.toLowerCase();
        return requests
            .filter((r) => r.employeeEmail.toLowerCase() === email)
            .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
    }, [requests, effectiveEmail]);
    const handleCreate = async () => {
        if (!apiToken || !startDate || !endDate)
            return;
        setSaving(true);
        try {
            const days = countBusinessDays(startDate, endDate);
            const manager = approbateurs.find((a) => a.role === "N+1");
            const id = await createLeaveRequest(apiToken, {
                EmployeeEmail: effectiveEmail,
                EmployeeName: effectiveName || effectiveEmail,
                LeaveType: leaveType,
                StartDate: startDate,
                EndDate: endDate,
                EntryMode: "full",
                DaysCount: days,
                Status: "En attente",
                Comment: comment,
                ManagerEmail: manager?.employeeEmail || "",
            });
            setRequests((prev) => [{
                    id, employeeEmail: effectiveEmail, employeeName: effectiveName || effectiveEmail,
                    leaveType, startDate, endDate, entryMode: "full",
                    daysCount: days, status: "En attente",
                    comment, managerEmail: manager?.employeeEmail || "",
                    createdAt: new Date().toISOString(),
                }, ...prev]);
            setDialogOpen(false);
            showMessage(`Demande de ${LEAVE_TYPES.find((t) => t.value === leaveType)?.label || leaveType} créée (${days} jour${days > 1 ? "s" : ""})`, "success");
        }
        catch (err) {
            showMessage(`Erreur : ${err instanceof Error ? err.message : "Erreur"}`, "error");
        }
        finally {
            setSaving(false);
        }
    };
    const handleCancel = async (req) => {
        if (!apiToken || !confirm("Annuler cette demande ?"))
            return;
        try {
            await updateLeaveRequestStatus(apiToken, req.id, "Annule");
            setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: "Annule" } : r));
            showMessage("Demande annulée", "success");
        }
        catch {
            showMessage("Erreur lors de l'annulation", "error");
        }
    };
    if (loading)
        return _jsx(Spinner, { label: "Chargement..." });
    return (_jsxs("div", { children: [_jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }, children: [_jsx("h1", { style: { margin: 0 }, children: "Mes absences" }), _jsx(Button, { icon: _jsx(AddRegular, {}), appearance: "primary", onClick: () => { setStartDate(""); setEndDate(""); setLeaveType("CP"); setComment(""); setDialogOpen(true); }, children: "Nouvelle demande" })] }), _jsxs(TabList, { selectedValue: absTab, onTabSelect: (_, d) => setAbsTab(d.value), style: { marginBottom: "12px" }, children: [_jsx(Tab, { value: "planning", icon: _jsx(CalendarMonthRegular, {}), children: "Mon planning" }), _jsxs(Tab, { value: "demandes", icon: _jsx(ClipboardTaskList16Regular, {}), children: ["Mes demandes (", myRequests.length, ")"] })] }), error && _jsx(MessageBar, { intent: "error", style: { marginBottom: "8px" }, children: _jsx(MessageBarBody, { children: error }) }), success && _jsx(MessageBar, { intent: "success", style: { marginBottom: "8px" }, children: _jsx(MessageBarBody, { children: success }) }), absTab === "planning" && _jsx(PlanningView, { fixedEmployeeEmail: effectiveEmail }), absTab === "demandes" && (_jsxs(_Fragment, { children: [_jsxs(Table, { "aria-label": "Mes demandes", size: "small", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHeaderCell, { children: "Type" }), _jsx(TableHeaderCell, { children: "Du" }), _jsx(TableHeaderCell, { children: "Au" }), _jsx(TableHeaderCell, { children: "Jours" }), _jsx(TableHeaderCell, { children: "Statut" }), _jsx(TableHeaderCell, { children: "Commentaire" }), _jsx(TableHeaderCell, { children: "Actions" })] }) }), _jsxs(TableBody, { children: [myRequests.map((r) => (_jsxs(TableRow, { children: [_jsx(TableCell, { children: _jsx(Badge, { appearance: "outline", children: r.leaveType }) }), _jsx(TableCell, { children: formatDateFR(r.startDate) }), _jsx(TableCell, { children: formatDateFR(r.endDate) }), _jsx(TableCell, { children: r.daysCount }), _jsx(TableCell, { children: _jsx(Badge, { color: (r.status === "Approuvé" || r.status === "Approuve") ? "success" : r.status.startsWith("En attente") ? "warning" : (r.status === "Annulé" || r.status === "Annule") ? "danger" : "informative", appearance: "filled", children: r.status }) }), _jsx(TableCell, { children: _jsx(Text, { size: 200, children: r.comment || "—" }) }), _jsx(TableCell, { children: r.status.startsWith("En attente") && (_jsx(Button, { size: "small", appearance: "subtle", icon: _jsx(DismissCircleRegular, {}), onClick: () => handleCancel(r), style: { color: "#dc2626" }, children: "Annuler" })) })] }, r.id))), myRequests.length === 0 && _jsx(TableRow, { children: _jsx(TableCell, { colSpan: 7, children: _jsx(Text, { style: { color: "#999" }, children: "Aucune demande" }) }) })] })] }), _jsx(Dialog, { open: dialogOpen, onOpenChange: (_, d) => { if (!d.open)
                            setDialogOpen(false); }, children: _jsx(DialogSurface, { children: _jsxs(DialogBody, { children: [_jsx(DialogTitle, { children: "Nouvelle demande d'absence" }), _jsx(DialogContent, { children: _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "12px" }, children: [_jsx(Field, { label: "Type d'absence", required: true, children: _jsx(Dropdown, { value: LEAVE_TYPES.find((t) => t.value === leaveType)?.label || "", selectedOptions: [leaveType], onOptionSelect: (_, d) => setLeaveType(d.optionValue || "CP"), children: LEAVE_TYPES.map((t) => _jsx(Option, { value: t.value, children: t.label }, t.value)) }) }), _jsxs("div", { style: { display: "flex", gap: "12px" }, children: [_jsx(Field, { label: "Date de d\u00E9but", required: true, children: _jsx(Input, { type: "date", value: startDate, onChange: (_, d) => setStartDate(d.value) }) }), _jsx(Field, { label: "Date de fin", required: true, children: _jsx(Input, { type: "date", value: endDate, onChange: (_, d) => setEndDate(d.value) }) }), startDate && endDate && (_jsx(Field, { label: "Jours ouvr\u00E9s", children: _jsx(Text, { size: 400, weight: "bold", children: countBusinessDays(startDate, endDate) }) }))] }), _jsx(Field, { label: "Commentaire", children: _jsx(Textarea, { value: comment, onChange: (_, d) => setComment(d.value), placeholder: "Motif (optionnel)...", rows: 2 }) })] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { appearance: "secondary", onClick: () => setDialogOpen(false), children: "Annuler" }), _jsx(Button, { appearance: "primary", onClick: handleCreate, disabled: saving || !startDate || !endDate, children: saving ? "Envoi..." : "Soumettre" })] })] }) }) })] }))] }));
};
